import { useState, useEffect } from 'react';

import { IdentifierDTO } from '@zazume/zzm-base';

import { ValidationStatus } from '#/components/verify';
import { API } from '#/lib/network/API';
import { DocumentIDType } from '#/models';

export interface UseFiscalInformationValidationProps {
  idNumber: string;
  name: string;
  idNumberType: DocumentIDType;
  organizationId: IdentifierDTO;
  dirtyFields: {
    name?: boolean;
    idNumberType?: boolean;
    idNumber?: boolean;
  };
}

export interface UseFiscalInformationValidationReturn {
  validationStatus: ValidationStatus;
  validateFiscalInformation: () => Promise<void>;
  fiscalInformationChanged: boolean;
}

export const useFiscalInformationValidation = ({
  idNumber,
  name,
  idNumberType,
  organizationId,
  dirtyFields
}: UseFiscalInformationValidationProps): UseFiscalInformationValidationReturn => {
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>('pending');
  const { name: dirtyName, idNumberType: dirtyIdNumberType, idNumber: dirtyIdNumber } = dirtyFields;

  useEffect(() => {
    if (dirtyName || dirtyIdNumberType || dirtyIdNumber) {
      setValidationStatus('pending');
    }
  }, [name, idNumberType, idNumber, dirtyName, dirtyIdNumberType, dirtyIdNumber]);

  const validateFiscalInformation = async () => {
    try {
      setValidationStatus('pending');

      const billingData = {
        idNumber,
        name,
        documentIDType: idNumberType
      };

      const response = await API.accounting.paymentsLedgerAccount.verifyBillingData(organizationId)(billingData);

      setValidationStatus(response.isValid ? 'success' : 'failed');
    } catch (error) {
      console.error('Error validating fiscal information:', error);
      setValidationStatus('failed');
    }
  };

  return {
    validationStatus,
    validateFiscalInformation,
    fiscalInformationChanged: Boolean(dirtyName || dirtyIdNumberType || dirtyIdNumber)
  };
};
